/**
 * P3-27: TradeButton —— race 面板中的贸易按钮。
 *
 * 迁移自 `com.nuclearunicorn.game.ui.TradeButton` +
 * `TradeButtonController`（js/diplomacy.js:1103-1200）。
 *
 * 行为对齐：
 * - 主按钮点击：`game.diplomacy.trade(race)`
 * - links：all（xMax）、50%、20%；百分比/数值文案随 `usePercentageConsumptionValues`
 *   互换；显示受 `showNonApplicableButtons` 或 `hasMultipleResources` 控制
 * - tooltip：`ButtonModernHelper.getTooltipHTML` 直取
 */
import { useCallback, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import { useTradeButtonModel, type RaceLike } from "./useTradeModel";

interface TradeButtonProps {
  raceName: string;
}

export function TradeButton({ raceName }: TradeButtonProps) {
  const { snap, modelRef, getTooltip } = useTradeButtonModel(raceName);

  const doTrade = useCallback(
    (amt?: number) => (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const game = getGame() as
        | (GamePage & {
            diplomacy?: {
              get: (name: string) => RaceLike;
              trade?: (race: RaceLike) => void;
              tradeMultiple?: (race: RaceLike, amt: number) => void;
              tradeAll?: (race: RaceLike) => void;
            };
          })
        | null;
      const race = game?.diplomacy?.get?.(raceName);
      if (!game?.diplomacy || !race) {
        return;
      }
      if (amt === undefined) {
        game.diplomacy.trade?.(race);
      } else if (amt === -1) {
        game.diplomacy.tradeAll?.(race);
      } else {
        game.diplomacy.tradeMultiple?.(race, amt);
      }
    },
    [raceName],
  );

  if (!snap || !snap.visible) {
    return null;
  }

  const allLink = { title: "x" + snap.tradeMax, handler: doTrade(-1) };
  const halfLink = snap.showHalf
    ? {
        title: snap.usePercentage ? "50%" : "x" + snap.tradeHalf,
        tooltip: snap.usePercentage ? "x" + snap.tradeHalf : "50%",
        handler: doTrade(snap.tradeHalf),
      }
    : null;
  const fifthLink = snap.showFifth
    ? {
        title: snap.usePercentage ? "20%" : "x" + snap.tradeFifth,
        tooltip: snap.usePercentage ? "x" + snap.tradeFifth : "20%",
        handler: doTrade(snap.tradeFifth),
      }
    : null;

  const links = [allLink, halfLink, fifthLink].filter(
    (l): l is { title: string; tooltip?: string; handler: (e: MouseEvent) => void } => l !== null,
  );

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      links={links}
      domClassName=" trade"
      getTooltip={getTooltip}
      onClick={doTrade()}
      getSelectedObject={() => modelRef.current}
    />
  );
}
